import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  Image,
  Link as ChakraLink,
  SimpleGrid,
  Text,
  VStack,
  Badge,
} from "@chakra-ui/react";
import {
  FaChalkboardTeacher,
  FaUserShield,
  FaSchool,
  FaUserTie,
  FaLock,
  FaFilePdf,
  FaChartBar,
  FaUsers,
  FaCheckCircle,
  FaRegQuestionCircle,
  FaArrowRight,
  FaRegEnvelope,
  FaWhatsapp,
  FaFacebook,
  FaTwitter,
} from "react-icons/fa";
import useThemeColors from "../../hooks/useThemeColors";

const features = [
  {
    icon: FaLock,
    title: "Secure Result Management",
    desc: "All student data is encrypted and protected. Only authorized users can access results.",
  },
  {
    icon: FaFilePdf,
    title: "Easy Bulk Export",
    desc: "Export results to PDF or Excel in one click. Save time and reduce errors.",
  },
  {
    icon: FaChartBar,
    title: "Performance Analytics",
    desc: "Visualize student and class performance with interactive charts and reports.",
  },
  {
    icon: FaUsers,
    title: "Multi-Role Access",
    desc: "Admins, teachers, and proprietors each have tailored dashboards and permissions.",
  },
  {
    icon: FaCheckCircle,
    title: "Custom Report Cards",
    desc: "Design report cards to match your school's branding and requirements.",
  },
  {
    icon: FaChalkboardTeacher,
    title: "Effortless Grading",
    desc: "An intuitive interface for teachers to enter scores and comments quickly.",
  },
];

const testimonials = [
  {
    quote:
      "HeadMasterClient has transformed how we manage results. Teachers love the easy grading, and parents get timely updates.",
    name: "Mrs. Adebayo",
    role: "School Administrator",
  },
  {
    quote:
      "No more lost report cards! The bulk export and analytics features are a game changer for our school.",
    name: "Mr. Okafor",
    role: "Proprietor",
  },
  {
    quote:
      "I can upload grades from anywhere, and the system generates beautiful report cards instantly.",
    name: "Ms. Bello",
    role: "Teacher",
  },
];

const faqs = [
  {
    q: "Can students and parents view past results?",
    a: "Yes! Students and parents can securely access past results online anytime.",
  },
  {
    q: "Does this support multiple schools or campuses?",
    a: "Absolutely. You can manage multiple schools or campuses from a single dashboard.",
  },
  {
    q: "How do parents sign up?",
    a: "Parents receive secure invitations from the school and can create their own accounts.",
  },
  {
    q: "Is my data safe?",
    a: "All data is encrypted and stored securely. We comply with local education data policies.",
  },
];

const LandingPage = () => {
  const colors = useThemeColors();

  return (
    <Box
      minH="100vh"
      bg={colors.background.primary}
      color={colors.text.primary}
    >
      {/* Hero Section */}
      <Box
        as="section"
        py={{ base: 20, md: 28 }}
        bg={colors.background.secondary}
      >
        <Container maxW="7xl">
          <Flex
            direction={{ base: "column", md: "row" }}
            align="center"
            justify="space-between"
            gap={10}
          >
            <VStack align="start" gap={6} maxW="lg">
              <Badge
                colorPalette="purple"
                size="lg"
                borderRadius="full"
                px={4}
                py={2}
                fontWeight="bold"
              >
                Empower Your School With Smarter Results
              </Badge>
              <Heading
                as="h1"
                size="3xl"
                fontWeight="extrabold"
                lineHeight="shorter"
                color={colors.text.primary}
                letterSpacing="-2px"
              >
                The All-in-One School Result App
              </Heading>
              <Text fontSize="xl" color={colors.text.secondary}>
                Manage, view, and interact with academic results and performance
                data.{" "}
                <Text as="span" color="purple.500" fontWeight="bold">
                  Built for schools, teachers, and parents.
                </Text>
              </Text>
              <HStack gap={4} pt={4}>
                <Button
                  colorPalette="purple"
                  size="lg"
                  px={10}
                  fontWeight="bold"
                  boxShadow="md"
                  borderRadius="full"
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "lg",
                  }}
                  onClick={() => (window.location.href = "/auth/register")}
                >
                  Get Started Free
                  <Icon as={FaArrowRight} ml={2} />
                </Button>
                <Button
                  variant="outline"
                  colorPalette="purple"
                  size="lg"
                  px={8}
                  borderRadius="full"
                  _hover={{ bg: colors.background.tertiary }}
                  onClick={() => (window.location.href = "/demo")}
                >
                  Request Demo
                </Button>
              </HStack>
            </VStack>
            <Box flex={1} display={{ base: "none", md: "block" }}>
              <Image
                src="/resultng-logo-light.png"
                alt="School Management Illustration"
                maxW="450px"
                mx="auto"
                mt={{ base: 10, md: 0 }}
                borderRadius="2xl"
                boxShadow="2xl"
              />
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* Problem & Solution Section */}
      <Box as="section" py={20} bg={colors.background.primary}>
        <Container maxW="5xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={16}>
            <VStack align="start" gap={6}>
              <Heading size="lg" color="blue.600" fontWeight="bold">
                The Problem
              </Heading>
              <Text fontSize="xl" color={colors.text.secondary}>
                Manual grading, lost report cards, and lack of communication
                slow down your school's progress. Teachers spend hours on
                paperwork, and parents struggle to stay informed.
              </Text>
            </VStack>
            <VStack align="start" gap={6}>
              <Heading size="lg" color="green.500" fontWeight="bold">
                Our Solution
              </Heading>
              <Text fontSize="xl" color={colors.text.secondary}>
                HeadMasterClient automates result management, enables instant
                report generation, and keeps everyone connected. Save time,
                reduce errors, and empower your school community.
              </Text>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Use Cases / Personas */}
      <Box as="section" py={20} bg={colors.background.secondary}>
        <Container maxW="6xl">
          <Heading
            as="h2"
            size="xl"
            textAlign="center"
            mb={14}
            color="blue.700"
            fontWeight="extrabold"
          >
            Who is HeadMasterClient for?
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 4 }} gap={10}>
            <VStack>
              <Icon as={FaSchool} w={14} h={14} color="blue.400" />
              <Text fontWeight="bold" fontSize="lg">
                Schools
              </Text>
              <Text
                textAlign="center"
                fontSize="md"
                color={colors.text.secondary}
              >
                Centralized result database, report generation, performance
                analytics.
              </Text>
            </VStack>
            <VStack>
              <Icon as={FaUserShield} w={14} h={14} color="purple.400" />
              <Text fontWeight="bold" fontSize="lg">
                Admins
              </Text>
              <Text
                textAlign="center"
                fontSize="md"
                color={colors.text.secondary}
              >
                Full control over users, settings, and reporting.
              </Text>
            </VStack>
            <VStack>
              <Icon as={FaChalkboardTeacher} w={14} h={14} color="green.400" />
              <Text fontWeight="bold" fontSize="lg">
                Teachers
              </Text>
              <Text
                textAlign="center"
                fontSize="md"
                color={colors.text.secondary}
              >
                Effortless grade submission, automated reports.
              </Text>
            </VStack>
            <VStack>
              <Icon as={FaUserTie} w={14} h={14} color="orange.400" />
              <Text fontWeight="bold" fontSize="lg">
                Parents
              </Text>
              <Text
                textAlign="center"
                fontSize="md"
                color={colors.text.secondary}
              >
                Instant access to results and progress updates.
              </Text>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Feature Highlights */}
      <Box as="section" py={20} bg={colors.background.primary}>
        <Container maxW="6xl">
          <Heading
            as="h2"
            size="xl"
            textAlign="center"
            mb={14}
            color="purple.700"
            fontWeight="extrabold"
          >
            Powerful Features for Every School
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} gap={10}>
            {features.map((feature) => (
              <Box
                key={feature.title}
                bg={colors.background.secondary}
                borderRadius="2xl"
                boxShadow="lg"
                p={10}
                textAlign="center"
                _hover={{
                  transform: "scale(1.04)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                }}
                transition="transform .2s ease, box-shadow .2s ease"
              >
                <Icon
                  as={feature.icon}
                  w={14}
                  h={14}
                  color="purple.500"
                  mb={4}
                />
                <Heading as="h3" size="md" mb={2} fontWeight="bold">
                  {feature.title}
                </Heading>
                <Text color={colors.text.secondary} fontSize="lg">
                  {feature.desc}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Demo/Preview Section */}
      <Box as="section" py={20} bg="purple.50">
        <Container maxW="5xl" textAlign="center">
          <Heading
            as="h2"
            size="lg"
            mb={8}
            color="purple.700"
            fontWeight="extrabold"
          >
            See HeadMasterClient in Action
          </Heading>
          <Box
            borderRadius="2xl"
            overflow="hidden"
            boxShadow="2xl"
            mb={8}
            bg={colors.background.primary}
          >
            {/* Replace with your own video or carousel if available */}
            <Image
              src="/resultng-logo-light.png"
              alt="App Screenshot"
              w="100%"
              maxH="320px"
              objectFit="contain"
            />
          </Box>
          <Button
            colorPalette="purple"
            size="lg"
            borderRadius="full"
            px={10}
            fontWeight="bold"
            boxShadow="lg"
            _hover={{ transform: "scale(1.05)", boxShadow: "2xl" }}
            onClick={() => (window.location.href = "/demo")}
          >
            Try Live Demo
            <Icon as={FaArrowRight} ml={2} />
          </Button>
        </Container>
      </Box>

      {/* Testimonials */}
      <Box as="section" py={20} bg={colors.background.primary}>
        <Container maxW="5xl">
          <Heading
            as="h2"
            size="xl"
            textAlign="center"
            mb={14}
            color="purple.700"
            fontWeight="extrabold"
          >
            What Our Users Say
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} gap={10}>
            {testimonials.map((t, i) => (
              <Box
                key={i}
                bg={colors.background.secondary}
                borderRadius="2xl"
                boxShadow="lg"
                p={8}
                textAlign="center"
                fontStyle="italic"
                _hover={{
                  transform: "scale(1.03)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                }}
                transition="transform .2s ease, box-shadow .2s ease"
              >
                <Text mb={4} fontSize="lg">
                  "{t.quote}"
                </Text>
                <Text fontWeight="bold">{t.name}</Text>
                <Text fontSize="sm" color="purple.500">
                  {t.role}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Comparison Section */}
      <Box as="section" py={20} bg={colors.background.secondary}>
        <Container maxW="5xl">
          <Heading
            as="h2"
            size="lg"
            textAlign="center"
            mb={10}
            color="purple.700"
            fontWeight="extrabold"
          >
            Why Choose HeadMasterClient?
          </Heading>
          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={10}>
            <Box>
              <Heading size="md" mb={4} fontWeight="bold">
                Manual Methods
              </Heading>
              <VStack align="start" gap={3}>
                <Text>❌ Paper-based grading</Text>
                <Text>❌ Lost or damaged report cards</Text>
                <Text>❌ Time-consuming and error-prone</Text>
                <Text>❌ No analytics or insights</Text>
              </VStack>
            </Box>
            <Box>
              <Heading size="md" mb={4} fontWeight="bold">
                HeadMasterClient
              </Heading>
              <VStack align="start" gap={3}>
                <Text>✅ Secure, digital result management</Text>
                <Text>✅ Instant report generation</Text>
                <Text>✅ Bulk export to PDF/Excel</Text>
                <Text>✅ Analytics and performance tracking</Text>
              </VStack>
            </Box>
          </Grid>
        </Container>
      </Box>

      {/* Security & Compliance */}
      <Box as="section" py={20} bg={colors.background.primary}>
        <Container maxW="4xl" textAlign="center">
          <HStack justify="center" mb={4}>
            <Icon as={FaLock} w={8} h={8} color="purple.400" />
            <Heading as="h2" size="md" color="purple.700" fontWeight="bold">
              Security & Compliance
            </Heading>
          </HStack>
          <Text fontSize="xl" mb={2}>
            Your data is encrypted and protected. We comply with GDPR and local
            education data policies.
          </Text>
          <Text fontSize="md" color={colors.text.secondary}>
            Only authorized users can access sensitive information. Privacy is
            our top priority.
          </Text>
        </Container>
      </Box>

      {/* Pricing / CTA */}
      <Box as="section" py={20} bg="purple.50">
        <Container maxW="3xl" textAlign="center">
          <Heading
            as="h2"
            size="lg"
            mb={4}
            color="purple.700"
            fontWeight="extrabold"
          >
            Start Free – No Credit Card Required
          </Heading>
          <Text fontSize="xl" mb={8}>
            Experience all features with no risk. Upgrade anytime for more users
            and advanced analytics.
          </Text>
          <Button
            colorPalette="purple"
            size="lg"
            px={12}
            fontWeight="bold"
            boxShadow="lg"
            borderRadius="full"
            _hover={{ transform: "scale(1.05)", boxShadow: "2xl" }}
            onClick={() => (window.location.href = "/signup")}
          >
            Sign Up Free
            <Icon as={FaArrowRight} ml={2} />
          </Button>
        </Container>
      </Box>

      {/* FAQs */}
      <Box as="section" py={20} bg={colors.background.primary}>
        <Container maxW="5xl">
          <Heading
            as="h2"
            size="xl"
            textAlign="center"
            mb={14}
            color="purple.700"
            fontWeight="extrabold"
          >
            Frequently Asked Questions
          </Heading>
          <VStack gap={8} align="stretch">
            {faqs.map((faq, i) => (
              <Box
                key={i}
                p={7}
                bg={colors.background.secondary}
                borderRadius="xl"
                boxShadow="md"
                _hover={{
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
                }}
                transition="transform .2s ease, box-shadow .2s ease"
              >
                <HStack>
                  <Icon as={FaRegQuestionCircle} color="purple.400" />
                  <Text fontWeight="bold" fontSize="lg">
                    {faq.q}
                  </Text>
                </HStack>
                <Text mt={2} fontSize="md">
                  {faq.a}
                </Text>
              </Box>
            ))}
          </VStack>
        </Container>
      </Box>

      {/* Contact/Support Section */}
      <Box as="section" py={20} bg={colors.background.secondary}>
        <Container maxW="3xl" textAlign="center">
          <Heading
            as="h2"
            size="lg"
            mb={4}
            color="purple.700"
            fontWeight="extrabold"
          >
            Need Help or Want to Report a Problem?
          </Heading>
          <Text fontSize="xl" mb={8}>
            Our support team is here for you. Reach out anytime!
          </Text>
          <HStack justify="center" gap={8}>
            <Button
              colorPalette="purple"
              variant="outline"
              size="lg"
              borderRadius="full"
              onClick={() =>
                window.open("mailto:support@headmasterclient.com", "_blank")
              }
            >
              <Icon as={FaRegEnvelope} mr={2} />
              Email Support
            </Button>
            <Button
              colorPalette="green"
              variant="outline"
              size="lg"
              borderRadius="full"
              onClick={() => window.open("https://wa.me/1234567890", "_blank")}
            >
              <Icon as={FaWhatsapp} mr={2} />
              WhatsApp
            </Button>
          </HStack>
        </Container>
      </Box>

      {/* Footer */}
      <Box as="footer" py={10} bg="gray.800" color="white">
        <Container maxW="6xl">
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align="center"
          >
            <Text fontWeight="bold" fontSize="lg">
              HeadMasterClient
            </Text>
            <HStack gap={8} mt={{ base: 4, md: 0 }}>
              <ChakraLink
                color="white"
                fontWeight="medium"
                onClick={() => (window.location.href = "/privacy")}
                cursor="pointer"
              >
                Privacy Policy
              </ChakraLink>
              <ChakraLink
                color="white"
                fontWeight="medium"
                onClick={() => (window.location.href = "/terms")}
                cursor="pointer"
              >
                Terms of Service
              </ChakraLink>
              <ChakraLink
                color="white"
                fontWeight="medium"
                onClick={() => (window.location.href = "/contact")}
                cursor="pointer"
              >
                Contact
              </ChakraLink>
              <ChakraLink href="https://facebook.com" color="white">
                <FaFacebook />
              </ChakraLink>
              <ChakraLink href="https://twitter.com" color="white">
                <FaTwitter />
              </ChakraLink>
            </HStack>
          </Flex>
          <Text mt={6} fontSize="sm" color="gray.300" textAlign="center">
            &copy; {new Date().getFullYear()} HeadMasterClient. All rights
            reserved.
          </Text>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
